/**
 * How the music is written down (ADR-0056): notes on a grid of beats, in the
 * seven notes of C major and nothing else, so every chime the game plays falls
 * in the key of whatever is playing under it.
 *
 * Pure data and arithmetic: no sound is made here, so the tests can read every
 * track note by note.
 */

/** The voices a track can use; each is made in `instruments.ts`. */
export type InstrumentName =
  | 'vibes'
  | 'gtr'
  | 'bass'
  | 'rim'
  | 'shaker'
  | 'marimba'
  | 'bassoon'
  | 'pizz'
  | 'wood'
  | 'harpsi'
  | 'clarinet'
  | 'flute'
  | 'pad'
  | 'wind'
  | 'tom';

export interface NoteEvent {
  /** When, in beats from the top of the loop, before swing. */
  readonly beat: number;
  readonly instrument: InstrumentName;
  /** The pitch as a MIDI number, or null for a drum. */
  readonly midi: number | null;
  /** How long, in beats. */
  readonly beats: number;
  readonly velocity: number;
  /** A strum: seconds after the beat, so a chord's strings sound one after another. */
  readonly offset: number;
}

export interface Track {
  readonly id: string;
  readonly bpm: number;
  readonly beatsPerBar: number;
  readonly bars: number;
  /** Where the off-beat eighth lands: 0.5 is straight, 0.6 a shuffle. */
  readonly swing: number;
  /** Measured offline so every track plays at the same loudness on a phone. */
  readonly trim: number;
  /** Every note of one loop, in order. */
  readonly events: readonly NoteEvent[];
}

const STEPS: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** "C4" is 60. Only natural notes: the music is written in C major's seven. */
export function midi(name: string): number {
  const match = /^([A-G])(-?\d)$/.exec(name);
  const step = match ? STEPS[match[1] as string] : undefined;
  if (!match || step === undefined) throw new Error(`not a note: ${name}`);
  return 12 * (Number(match[2]) + 1) + step;
}

export const hz = (note: number): number => 440 * 2 ** ((note - 69) / 12);

/** C major's seven notes, by semitone above C. */
export const SCALE: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

/** A note moved `steps` along the scale: 2 is a third up, -2 a third down. */
export function diatonic(note: number, steps: number): number {
  const index = SCALE.indexOf(((note % 12) + 12) % 12);
  if (index < 0) throw new Error(`not in the scale: ${note}`);
  const to = index + steps;
  const octave = Math.floor(note / 12) + Math.floor(to / 7);
  return octave * 12 + (SCALE[((to % 7) + 7) % 7] as number);
}

/** A track being written: events in any order, sorted when it is done. */
export class Score {
  readonly events: NoteEvent[] = [];

  constructor(readonly beatsPerBar: number) {}

  /** One note or drum hit. */
  add(
    beat: number,
    instrument: InstrumentName,
    note: string | null,
    beats: number,
    velocity = 1,
    offset = 0,
  ): void {
    this.events.push({
      beat,
      instrument,
      midi: note === null ? null : midi(note),
      beats,
      velocity,
      offset,
    });
  }

  /** The same, by MIDI number: for lines worked out rather than written. */
  addNote(
    beat: number,
    instrument: InstrumentName,
    note: number,
    beats: number,
    velocity = 1,
  ): void {
    this.events.push({ beat, instrument, midi: note, beats, velocity, offset: 0 });
  }

  /** A chord, all at once or strummed `strum` seconds apart. */
  chord(
    beat: number,
    instrument: InstrumentName,
    notes: readonly string[],
    beats: number,
    velocity: number,
    strum = 0,
  ): void {
    notes.forEach((note, k) => {
      this.add(beat, instrument, note, beats, velocity, k * strum);
    });
  }

  /**
   * A line of notes: `note/length` in units of `unit` beats, `r` for a rest.
   * A `|` is a bar line and is checked, so a miscounted bar fails loudly
   * instead of drifting the rest of the tune. Returns the beat it ends on.
   */
  line(
    instrument: InstrumentName,
    start: number,
    text: string,
    unit: number,
    velocity = 1,
    legato = 0.92,
  ): number {
    let beat = start;
    for (const token of text.trim().split(/\s+/)) {
      if (token === '|') {
        const bars = (beat - start) / this.beatsPerBar;
        if (Math.abs(bars - Math.round(bars)) > 1e-6) {
          throw new Error(`${instrument}: a bar line at beat ${beat}`);
        }
        continue;
      }
      const [name = '', size = ''] = token.split('/');
      const length = Number(size) * unit;
      if (!(length > 0)) throw new Error(`${instrument}: no length in ${token}`);
      if (name !== 'r') this.add(beat, instrument, name, length * legato, velocity);
      beat += length;
    }
    return beat;
  }

  /** The finished loop, checked to end where the track says it does. */
  done(id: string, bpm: number, bars: number, swing: number, trim: number): Track {
    const length = bars * this.beatsPerBar;
    const late = this.events.find((e) => e.beat >= length);
    if (late) throw new Error(`${id}: a note at beat ${late.beat}, past the loop`);
    const events = [...this.events].sort((a, b) => a.beat - b.beat || a.offset - b.offset);
    return { id, bpm, beatsPerBar: this.beatsPerBar, bars, swing, trim, events };
  }
}

/** Swing: the off-beat eighth lands late, by the track's amount. */
export function swung(beat: number, swing: number): number {
  if (swing === 0.5) return beat;
  const whole = Math.floor(beat);
  const f = beat - whole;
  return whole + (f < 0.5 ? (f / 0.5) * swing : swing + ((f - 0.5) / 0.5) * (1 - swing));
}

/** How long one loop of a track lasts, in seconds. */
export const loopSeconds = (track: Track): number =>
  (track.bars * track.beatsPerBar * 60) / track.bpm;
